import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import usePageTitle from 'hooks/usePageTitle';
import useEscapeClose from 'hooks/useEscapeClose';

const TYPES = [
  { value: 'entrant', label: 'Entrant' },
  { value: 'sortant', label: 'Sortant' },
];

const STATUTS = [
  { value: 'recu', label: 'Recu', badge: 'info' },
  { value: 'en_cours', label: 'En cours', badge: 'warning' },
  { value: 'traite', label: 'Traite', badge: 'success' },
  { value: 'archive', label: 'Archive', badge: '' },
];

const URGENCES = [
  { value: 'normale', label: 'Normale' },
  { value: 'urgente', label: 'Urgente' },
  { value: 'tres_urgente', label: 'Tres urgente' },
];

const EMPTY = { type: 'entrant', expediteur: '', destinataire: '', objet: '', date_courrier: '', date_reception: '', urgence: 'normale', notes: '' };

export default function Courriers() {
  usePageTitle('Courriers');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ type: '', statut: '', urgence: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [listRes, statsRes] = await Promise.all([
        api.get('/assistanat/courriers', { params }),
        api.get('/assistanat/courriers/stats'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.objet) return alert('Type et objet requis');
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/assistanat/courriers/${editId}`, form);
      } else {
        await api.post('/assistanat/courriers', form);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      type: item.type, expediteur: item.expediteur || '', destinataire: item.destinataire || '',
      objet: item.objet || '', date_courrier: item.date_courrier?.split('T')[0] || '',
      date_reception: item.date_reception?.split('T')[0] || '', urgence: item.urgence || 'normale',
      notes: item.notes || '', statut: item.statut,
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce courrier ?')) return;
    try {
      await api.delete(`/assistanat/courriers/${id}`);
      load();
    } catch {}
  };

  const updateStatut = async (id, statut) => {
    try {
      await api.put(`/assistanat/courriers/${id}`, { statut });
      load();
    } catch {}
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const setFld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const statutBadge = (s) => {
    const found = STATUTS.find(x => x.value === s);
    return found ? found.badge : 'info';
  };

  return (
    <div>
<div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></div></div>
          <div className="stat-card-value">{stats.total || 0}</div>
          <div className="stat-card-sub">courriers</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Entrants</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-primary)' }}>{stats.entrants || 0}</div>
          <div className="stat-card-sub">courriers recus</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Sortants</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-info)' }}>{stats.sortants || 0}</div>
          <div className="stat-card-sub">courriers envoyes</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">En cours</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{stats.en_cours || 0}</div>
          <div className="stat-card-sub">a traiter</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="form-input" placeholder="Rechercher..." value={filters.search}
              onChange={e => setF('search', e.target.value)} style={{ maxWidth: 200 }} />
            <select className="form-input" value={filters.type} onChange={e => setF('type', e.target.value)} style={{ maxWidth: 140 }}>
              <option value="">Tous types</option>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className="form-input" value={filters.statut} onChange={e => setF('statut', e.target.value)} style={{ maxWidth: 140 }}>
              <option value="">Tous statuts</option>
              {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select className="form-input" value={filters.urgence} onChange={e => setF('urgence', e.target.value)} style={{ maxWidth: 150 }}>
              <option value="">Toutes urgences</option>
              {URGENCES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            + Nouveau courrier
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Type</th>
                  <th>Expediteur</th>
                  <th>Destinataire</th>
                  <th>Objet</th>
                  <th>Date courrier</th>
                  <th>Urgence</th>
                  <th>Statut</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Aucun courrier</td></tr>
                )}
                {items.map(item => (
                  <tr key={item.id}>
                    <td><strong>{item.numero}</strong></td>
                    <td>
                      <span className={`gss-badge ${item.type === 'entrant' ? 'gss-badge-green' : 'gss-badge-blue'}`}>
                        {item.type === 'entrant' ? 'Entrant' : 'Sortant'}
                      </span>
                    </td>
                    <td>{item.expediteur || '-'}</td>
                    <td>{item.destinataire || '-'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.objet}</td>
                    <td>{item.date_courrier ? new Date(item.date_courrier).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>
                      {item.urgence === 'tres_urgente' && <span className="gss-badge" style={{ background: '#FEE2E2', color: '#DC2626' }}>Tres urgente</span>}
                      {item.urgence === 'urgente' && <span className="gss-badge" style={{ background: '#FFF3E0', color: '#F57C00' }}>Urgente</span>}
                      {item.urgence === 'normale' && <span className="gss-badge gss-badge-grey">Normale</span>}
                    </td>
                    <td><span className={`gss-badge gss-badge-${statutBadge(item.statut)}`}>{item.statut}</span></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="gss-actions">
                        {item.statut === 'recu' && <button className="gss-btn-sm" style={{ background: '#FFF3E0', color: '#F57C00' }} onClick={() => updateStatut(item.id, 'en_cours')}>En cours</button>}
                        {item.statut === 'en_cours' && <button className="gss-btn-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }} onClick={() => updateStatut(item.id, 'traite')}>Traite</button>}
                        <button className="gss-btn-icon" onClick={() => handleEdit(item)} title="Modifier">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => handleDelete(item.id)} title="Archiver">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FORMULAIRE MODAL */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <h2>{editId ? 'Modifier le courrier' : 'Nouveau courrier'}</h2>
                <div className="gss-modal-subtitle">Gestion du courrier</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span>INFORMATIONS GENERALES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label className="form-label">Type *</label>
                    <select className="form-input" value={form.type} onChange={e => setFld('type', e.target.value)} required>
                      {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Urgence</label>
                    <select className="form-input" value={form.urgence} onChange={e => setFld('urgence', e.target.value)}>
                      {URGENCES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>CORRESPONDANTS</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label className="form-label">Expediteur</label>
                    <input className="form-input" value={form.expediteur} onChange={e => setFld('expediteur', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Destinataire</label>
                    <input className="form-input" value={form.destinataire} onChange={e => setFld('destinataire', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Objet *</label>
                    <input className="form-input" value={form.objet} onChange={e => setFld('objet', e.target.value)} required />
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span>DATES & SUIVI</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label className="form-label">Date courrier</label>
                    <input className="form-input" type="date" value={form.date_courrier} onChange={e => setFld('date_courrier', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Date reception</label>
                    <input className="form-input" type="date" value={form.date_reception} onChange={e => setFld('date_reception', e.target.value)} />
                  </div>
                  {editId && (
                    <div className="gss-field">
                      <label className="form-label">Statut</label>
                      <select className="form-input" value={form.statut} onChange={e => setFld('statut', e.target.value)}>
                        {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows="3" value={form.notes} onChange={e => setFld('notes', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> Enregistrer</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
